import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Instagram, Facebook, Image, Video, LogIn, LogOut,
  UploadCloud, Trash2, CheckCircle, AlertCircle, Send,
  User, Key, MessageSquare, RefreshCw, Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://rnd.kalptree.xyz/api';

const SocialPosting = () => {
  const [platform, setPlatform] = useState('instagram');
  const [apiType, setApiType] = useState('official'); // 'official' or 'legacy'
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [result, setResult] = useState(null);
  const [notification, setNotification] = useState(null);

  // Official API state
  const [metaConnected, setMetaConnected] = useState(false);
  const [metaPages, setMetaPages] = useState([]);
  const [selectedMetaPage, setSelectedMetaPage] = useState('');
  const [selectedMetaInstagram, setSelectedMetaInstagram] = useState('');

  // Legacy Login form
  const [loginMode, setLoginMode] = useState('password'); // 'password' or 'sessionid'
  const [loginForm, setLoginForm] = useState({ username: '', password: '', sessionId: '' });
  const [showChallenge, setShowChallenge] = useState(false);
  const [challengeCode, setChallengeCode] = useState('');

  // Post form
  const [postForm, setPostForm] = useState({
    account: [],
    caption: '',
    mediaFile: null,
    mediaPreviewUrl: '',
    mediaType: 'photo',
  });

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  const apiFetch = useCallback(async (path, options = {}) => {
    let cleanPath = path;
    const cleanBase = API_URL.replace(/\/+$/, '');
    if (cleanBase.endsWith('/api') && cleanPath.startsWith('/api/')) {
      cleanPath = cleanPath.replace(/^\/api/, '');
    }
    const url = `${cleanBase}${cleanPath.startsWith('/') ? '' : '/'}${cleanPath}`;
    const defaultHeaders = {};
    if (options.body && !(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }
    const res = await fetch(url, { ...options, headers: { ...defaultHeaders, ...options.headers } });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || data.message || 'Request failed');
    return data;
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const data = await apiFetch('/api/social/instagram/accounts');
      setAccounts(data.accounts || []);
    } catch { setAccounts([]); }
  }, [apiFetch]);

  const loadMetaStatus = useCallback(async () => {
    try {
      const data = await apiFetch('/api/social/meta/status');
      setMetaConnected(!!data.connected);
      const pages = data.pages || [];
      setMetaPages(pages);
      if (pages.length > 0) {
        setSelectedMetaPage(pages[0].id);
        if (pages[0].instagram_accounts && pages[0].instagram_accounts.length > 0) {
          setSelectedMetaInstagram(pages[0].instagram_accounts[0].id);
        } else {
          setSelectedMetaInstagram('');
        }
      }
    } catch {
      setMetaConnected(false);
      setMetaPages([]);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadAccounts();
    loadMetaStatus();
  }, [loadAccounts, loadMetaStatus]);

  useEffect(() => {
    if (platform === 'instagram') {
      if (accounts.length === 1 && postForm.account.length === 0) {
        setPostForm(prev => ({ ...prev, account: [`${accounts[0].username}|`] }));
      } else if (accounts.length === 0 && postForm.account.length > 0) {
        setPostForm(prev => ({ ...prev, account: [] }));
      }
    }
  }, [accounts, platform, postForm.account]);

  const handleMetaPageChange = (pageId) => {
    setSelectedMetaPage(pageId);
    const pg = metaPages.find(p => p.id === pageId);
    if (pg && pg.instagram_accounts && pg.instagram_accounts.length > 0) {
      setSelectedMetaInstagram(pg.instagram_accounts[0].id);
    } else {
      setSelectedMetaInstagram('');
    }
  };

  const handleConnectMeta = async () => {
    setLoading(true);
    try {
      const creds = await apiFetch('/api/meta/credentials-status');
      if (!creds.configured) {
        showNotification('Meta Developer App credentials not configured. Please configure them in the Meta Ads dashboard first.', 'error');
        setLoading(false);
        return;
      }
      const data = await apiFetch('/api/meta/auth-url');
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else if (data.connected) {
        showNotification('Already connected to Meta!', 'success');
        loadMetaStatus();
      }
    } catch (err) {
      showNotification(err.message, 'error');
    }
    setLoading(false);
  };

  const handleLogin = async () => {
    if (!loginForm.username || (loginMode === 'password' && !loginForm.password) || (loginMode === 'sessionid' && !loginForm.sessionId)) {
      showNotification('Enter username and password or session ID.', 'error');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('username', loginForm.username);
      if (loginMode === 'password') {
        fd.append('password', loginForm.password);
      } else {
        fd.append('session_id', loginForm.sessionId);
      }
      const data = await apiFetch('/api/social/instagram/login', { method: 'POST', body: fd });
      if (data.status === 'challenge_required' || data.status === 'two_factor_required') {
        setShowChallenge(true);
        const msg = data.status === 'two_factor_required'
          ? 'Two-factor authentication required. Enter your 6-digit code from the authenticator app.'
          : 'Instagram sent a verification code. Check your app/email.';
        showNotification(msg, 'info');
      } else if (data.status === 'feedback_required') {
        showNotification(data.message || 'Instagram temporarily blocked login. Try from the IG app first, wait 30 min, then retry.', 'error');
      } else {
        showNotification(`Logged in as @${data.username}`, 'success');
        setLoginForm({ username: '', password: '', sessionId: '' });
        loadAccounts();
      }
    } catch (err) {
      showNotification(err.message, 'error');
    }
    setLoading(false);
  };

  const handleChallengeSubmit = async () => {
    if (!challengeCode) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('username', loginForm.username);
      fd.append('password', loginForm.password);
      fd.append('verification_code', challengeCode);
      const data = await apiFetch('/api/social/instagram/login/challenge', { method: 'POST', body: fd });
      showNotification('Challenge resolved! Logged in.', 'success');
      setShowChallenge(false);
      setChallengeCode('');
      setLoginForm({ username: '', password: '' });
      loadAccounts();
    } catch (err) {
      showNotification(err.message, 'error');
    }
    setLoading(false);
  };

  const handleLogout = async (username) => {
    try {
      const fd = new FormData();
      fd.append('username', username);
      await apiFetch('/api/social/instagram/logout', { method: 'POST', body: fd });
      showNotification(`@${username} logged out.`, 'info');
      loadAccounts();
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleMediaFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    setPostForm(prev => ({
      ...prev,
      mediaFile: file,
      mediaPreviewUrl: URL.createObjectURL(file),
      mediaType: isVideo ? 'video' : 'photo',
    }));
  };

  const handleRemoveMedia = () => {
    setPostForm(prev => ({
      ...prev,
      mediaFile: null,
      mediaPreviewUrl: '',
      mediaType: 'photo',
    }));
  };

  const handlePost = async () => {
    if (!postForm.account || postForm.account.length === 0) {
      showNotification('Select at least one account to post from.', 'error');
      return;
    }
    if (!postForm.mediaFile) {
      showNotification('Select a photo or video to post.', 'error');
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const results = [];
      const errors = [];

      for (const accountValue of postForm.account) {
        const [username, password] = accountValue.split('|');
        const endpoint = postForm.mediaType === 'video'
          ? '/api/social/instagram/post/video'
          : '/api/social/instagram/post/photo';

        const fd = new FormData();
        fd.append('username', username);
        fd.append('password', password || '');
        fd.append('caption', postForm.caption);
        fd.append(postForm.mediaType === 'video' ? 'video' : 'image', postForm.mediaFile);

        try {
          const data = await apiFetch(endpoint, { method: 'POST', body: fd });
          results.push({ username, success: true, data });
        } catch (err) {
          errors.push({ username, success: false, error: err.message });
        }
      }

      setResult({ results, errors });

      if (errors.length === 0) {
        showNotification('Posted to all selected accounts successfully!', 'success');
        setPostForm(prev => ({ ...prev, mediaFile: null, mediaPreviewUrl: '', caption: '' }));
      } else if (results.length > 0) {
        showNotification(`Posted to some accounts. Failed for: ${errors.map(e => '@' + e.username).join(', ')}`, 'warning');
      } else {
        showNotification(`Failed to post to any accounts: ${errors.map(e => e.error).join('; ')}`, 'error');
      }
    } catch (err) {
      showNotification(err.message, 'error');
    }
    setLoading(false);
  };

  const handleOfficialPost = async () => {
    if (platform === 'instagram' && !selectedMetaInstagram) {
      showNotification('Please select a linked Instagram Professional account.', 'error');
      return;
    }
    if (!postForm.mediaFile) {
      showNotification('Please select a photo or video to post.', 'error');
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('platform', platform);
      fd.append('page_id', selectedMetaPage);
      if (platform === 'instagram') {
        fd.append('instagram_account_id', selectedMetaInstagram);
      }
      fd.append('caption', postForm.caption);
      fd.append('media', postForm.mediaFile);

      const data = await apiFetch('/api/social/meta/post', {
        method: 'POST',
        body: fd
      });
      setResult(data);
      showNotification(data.message || 'Posted successfully!', 'success');
      setPostForm(prev => ({ ...prev, mediaFile: null, mediaPreviewUrl: '', caption: '' }));
    } catch (err) {
      showNotification(err.message, 'error');
    }
    setLoading(false);
  };

  // ---------------------------------------------------------------------------
  // Render sub-components
  // ---------------------------------------------------------------------------

  const renderOfficialAPISection = () => {
    if (!metaConnected) {
      return (
        <div className="text-center py-16 text-gray-500 bg-gray-950/20 border border-gray-800 rounded-xl space-y-4">
          <Globe className="w-14 h-14 mx-auto opacity-30 text-blue-400" />
          <div className="max-w-md mx-auto px-4">
            <h3 className="text-base font-bold text-gray-200 mb-1">Official Meta Graph API Connection</h3>
            <p className="text-xs text-gray-400 leading-relaxed mb-6">
              Connect your Meta (Facebook) account via OAuth to unlock safe, official, and direct content publishing to your Instagram Business/Creator account or Facebook Pages.
            </p>
            <button
              onClick={handleConnectMeta}
              disabled={loading}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm rounded-lg transition-all shadow-lg flex items-center justify-center gap-2 mx-auto shadow-blue-900/20"
            >
              {loading ? 'Redirecting...' : 'Connect Meta Account'}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={16} className="text-green-400" />
            <h3 className="text-sm font-bold text-gray-200">Select Target Destination</h3>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">
              Facebook Page
            </label>
            <select
              value={selectedMetaPage}
              onChange={(e) => handleMetaPageChange(e.target.value)}
              className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-pink-500 outline-none"
            >
              {metaPages.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {platform === 'instagram' && (
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">
                Instagram Professional Account
              </label>
              {selectedMetaInstagram ? (
                <select
                  value={selectedMetaInstagram}
                  onChange={(e) => setSelectedMetaInstagram(e.target.value)}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-pink-500 outline-none"
                >
                  {metaPages.find(p => p.id === selectedMetaPage)?.instagram_accounts?.map(ig => (
                    <option key={ig.id} value={ig.id}>@{ig.username || ig.name || ig.id}</option>
                  ))}
                </select>
              ) : (
                <div className="p-3.5 bg-yellow-900/10 border border-yellow-500/20 text-yellow-400 text-xs rounded-lg leading-relaxed">
                  ⚠️ No linked Instagram professional account found for this page. Make sure you have connected an Instagram Creator or Business account in your Facebook Page settings.
                </div>
              )}
            </div>
          )}

          <div className="pt-4 border-t border-gray-800">
            <button
              onClick={handleConnectMeta}
              className="text-xs text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1.5 transition-all"
            >
              <RefreshCw size={12} /> Reconnect or Change Facebook Pages
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-1">
            <Send size={16} className="text-pink-400" />
            <h3 className="text-sm font-bold text-gray-200">Create Post (Official API)</h3>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">
              Caption / Message
            </label>
            <textarea
              value={postForm.caption}
              onChange={(e) => setPostForm(prev => ({ ...prev, caption: e.target.value }))}
              className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-pink-500 outline-none h-24 resize-none"
              placeholder={platform === 'instagram' ? "Write your Instagram caption..." : "Write your Facebook page post..."}
            />
          </div>

          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">
              {postForm.mediaType === 'video' ? <Video size={12} className="inline mr-1" /> : <Image size={12} className="inline mr-1" />}
              {' '}Media ({postForm.mediaType === 'video' ? 'Video' : 'Photo'})
            </label>
            <div className="relative group">
              <div className="w-full h-32 bg-gray-950 border-2 border-dashed border-gray-700 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-pink-500 transition-all">
                {postForm.mediaPreviewUrl ? (
                  postForm.mediaType === 'video' ? (
                    <video src={postForm.mediaPreviewUrl} className="h-full w-full object-cover rounded-lg" controls />
                  ) : (
                    <img src={postForm.mediaPreviewUrl} alt="Preview" className="h-full w-full object-cover rounded-lg" />
                  )
                ) : (
                  <>
                    <UploadCloud className="w-8 h-8 text-gray-500 mb-1" />
                    <span className="text-xs text-gray-500 font-medium">Click to upload photo or video</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleMediaFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
            </div>
            {postForm.mediaPreviewUrl && (
              <button
                onClick={handleRemoveMedia}
                className="mt-2 text-xs px-3 py-1.5 rounded-lg bg-red-600/10 text-red-400 hover:bg-red-600/20 border border-red-500/20 font-medium flex items-center gap-1.5 transition-all"
              >
                <Trash2 size={12} /> Remove Media
              </button>
            )}
          </div>

          <button
            onClick={handleOfficialPost}
            disabled={loading || !postForm.mediaFile || (platform === 'instagram' && !selectedMetaInstagram)}
            className={`w-full font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg ${(loading || !postForm.mediaFile || (platform === 'instagram' && !selectedMetaInstagram))
                ? 'bg-gray-800 text-gray-500 cursor-not-allowed opacity-50'
                : platform === 'instagram'
                  ? 'bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white shadow-pink-900/20'
                  : 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-950/20'
              }`}
          >
            {loading ? 'Posting...' : (
              platform === 'instagram' ? <><Instagram size={18} /> Post to Instagram</> : <><Facebook size={18} /> Post to Facebook Page</>
            )}
          </button>

          {result && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-900/10 border border-green-500/30 rounded-xl p-4 space-y-2"
            >
              <p className="text-sm font-bold text-green-400 flex items-center gap-2">
                <CheckCircle size={16} /> Posted Successfully!
              </p>
              {result.url && (
                <a href={result.url} target="_blank" rel="noreferrer"
                  className="text-xs text-blue-400 underline flex items-center gap-1"
                >
                  <Globe size={12} /> View Post on Platform
                </a>
              )}
            </motion.div>
          )}
        </div>
      </div>
    );
  };

  const renderLoginSection = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LogIn size={16} className="text-blue-400" />
          <h3 className="text-sm font-bold text-gray-200">Login to Instagram</h3>
        </div>
        <div className="flex gap-1.5 p-0.5 bg-gray-950 rounded-lg border border-gray-800 text-[10px]">
          <button
            onClick={() => setLoginMode('password')}
            className={`py-1 px-2.5 rounded-md font-bold transition-all ${loginMode === 'password' ? 'bg-pink-600/20 border border-pink-500/30 text-pink-400' : 'text-gray-400'}`}
          >
            Password
          </button>
          <button
            onClick={() => setLoginMode('sessionid')}
            className={`py-1 px-2.5 rounded-md font-bold transition-all ${loginMode === 'sessionid' ? 'bg-pink-600/20 border border-pink-500/30 text-pink-400' : 'text-gray-400'}`}
          >
            Session ID Bypass
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">
            <User size={12} className="inline mr-1" /> Username
          </label>
          <input
            type="text"
            value={loginForm.username}
            onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
            className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-pink-500 outline-none"
            placeholder="@username"
          />
        </div>
        <div>
          {loginMode === 'password' ? (
            <>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">
                <Key size={12} className="inline mr-1" /> Password
              </label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-pink-500 outline-none"
                placeholder="Password"
              />
            </>
          ) : (
            <>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">
                <Key size={12} className="inline mr-1" /> Session ID
              </label>
              <input
                type="text"
                value={loginForm.sessionId || ''}
                onChange={(e) => setLoginForm(prev => ({ ...prev, sessionId: e.target.value }))}
                className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-pink-500 outline-none"
                placeholder="Paste sessionid cookie"
              />
            </>
          )}
        </div>
      </div>

      {loginMode === 'sessionid' && (
        <p className="text-[11px] text-gray-400 leading-relaxed bg-gray-950/40 p-2.5 rounded-lg border border-gray-900">
          💡 <span className="font-semibold text-gray-300">Instagram Login Bypass:</span> Log in to Instagram on your computer's browser, open Developer Console (<kbd className="bg-gray-900 px-1 py-0.5 rounded text-[10px]">F12</kbd> or <kbd className="bg-gray-900 px-1 py-0.5 rounded text-[10px]">Cmd+Option+I</kbd>), go to the <span className="font-medium text-pink-400">Application</span> (or <span className="font-medium text-pink-400">Storage</span>) tab → <span className="font-medium text-pink-400">Cookies</span> → <span className="font-medium text-pink-400">https://www.instagram.com</span>, and copy the value of the <code className="text-pink-400 font-mono font-semibold">sessionid</code> cookie.
        </p>
      )}

      <button
        onClick={handleLogin}
        disabled={loading || !loginForm.username || (loginMode === 'password' && !loginForm.password) || (loginMode === 'sessionid' && !loginForm.sessionId)}
        className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white font-bold py-2.5 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-pink-900/20"
      >
        {loading ? 'Logging in...' : <><Instagram size={16} /> Login to Instagram</>}
      </button>

      {showChallenge && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-yellow-900/10 border border-yellow-500/30 rounded-xl p-4 space-y-3"
        >
          <p className="text-xs text-yellow-400 font-medium">
            Enter the verification code sent to your email/phone or from your authenticator app.
          </p>
          <div className="flex gap-3">
            <input
              type="text"
              value={challengeCode}
              onChange={(e) => setChallengeCode(e.target.value)}
              className="flex-1 bg-gray-950 border border-yellow-600/50 rounded-lg p-3 text-sm text-gray-200 focus:border-yellow-500 outline-none"
              placeholder="6-digit code"
            />
            <button
              onClick={handleChallengeSubmit}
              disabled={loading || !challengeCode}
              className="px-5 py-2.5 rounded-lg bg-yellow-600 hover:bg-yellow-500 text-white font-bold text-sm transition-all disabled:opacity-50"
            >
              Verify
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );

  const renderAccountsSection = () => {
    if (accounts.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Instagram className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No Instagram accounts connected.</p>
          <p className="text-xs mt-1">Login with username & password above.</p>
        </div>
      );
    }
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2">
          <CheckCircle size={14} className="text-green-400" /> Connected Accounts
        </h3>
        {accounts.map(acct => (
          <div key={acct.username}
            className="flex items-center justify-between bg-gray-950/50 border border-gray-700/50 rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center font-bold text-sm text-white">
                {acct.username[0].toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-bold text-gray-200">@{acct.username}</p>
                <p className="text-xs text-green-400 flex items-center gap-1">
                  <CheckCircle size={10} /> Session saved
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPostForm(prev => ({ ...prev, account: [`${acct.username}|`] }))}
                className="text-xs px-3 py-1.5 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-500/30 font-medium flex items-center gap-1.5 transition-all"
              >
                <Send size={12} /> Post
              </button>
              <button
                onClick={() => handleLogout(acct.username)}
                className="text-xs px-3 py-1.5 rounded-lg bg-red-600/10 text-red-400 hover:bg-red-600/20 border border-red-500/20 font-medium flex items-center gap-1.5 transition-all"
              >
                <LogOut size={12} /> Logout
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPostForm = () => (
    <div className="space-y-5">
      <div className="flex items-center gap-2 mb-2">
        <Send size={16} className="text-pink-400" />
        <h3 className="text-sm font-bold text-gray-200">Create Instagram Post (Automation)</h3>
      </div>
      {accounts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider block">
              <User size={12} className="inline mr-1" /> Post as (Select one or more)
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPostForm(prev => ({ ...prev, account: accounts.map(a => `${a.username}|`) }))}
                className="text-[10px] text-pink-400 hover:text-pink-300 font-bold transition-colors"
              >
                Select All
              </button>
              <span className="text-[10px] text-gray-600">|</span>
              <button
                type="button"
                onClick={() => setPostForm(prev => ({ ...prev, account: [] }))}
                className="text-[10px] text-pink-400 hover:text-pink-300 font-bold transition-colors"
              >
                Deselect All
              </button>
            </div>
          </div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto border border-gray-800 rounded-lg p-2 bg-gray-950/70">
            {accounts.map(a => {
              const val = `${a.username}|`;
              const isSelected = postForm.account.includes(val);
              return (
                <label
                  key={a.username}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border cursor-pointer transition-all ${isSelected
                      ? 'bg-pink-600/10 border-pink-500/30 text-pink-400 font-medium shadow-sm shadow-pink-900/10'
                      : 'bg-gray-900/30 border-gray-800 text-gray-300 hover:bg-gray-900/50 hover:border-gray-700'
                    }`}
                >
                  <div className="flex items-center gap-2.5">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {
                        setPostForm(prev => {
                          const current = [...prev.account];
                          if (isSelected) {
                            return { ...prev, account: current.filter(x => x !== val) };
                          } else {
                            return { ...prev, account: [...current, val] };
                          }
                        });
                      }}
                      className="accent-pink-500 h-4 w-4 rounded border-gray-700 bg-gray-950 focus:ring-0 cursor-pointer"
                    />
                    <span className="text-sm">@{a.username}</span>
                  </div>
                  {isSelected && <CheckCircle size={14} className="text-pink-500" />}
                </label>
              );
            })}
          </div>
        </div>
      )}
      <div>
        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">
          <MessageSquare size={12} className="inline mr-1" /> Caption
        </label>
        <textarea
          value={postForm.caption}
          onChange={(e) => setPostForm(prev => ({ ...prev, caption: e.target.value }))}
          className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:border-pink-500 outline-none h-24 resize-none"
          placeholder="Write your caption... #hashtags"
        />
      </div>
      <div>
        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">
          {postForm.mediaType === 'video' ? <Video size={12} className="inline mr-1" /> : <Image size={12} className="inline mr-1" />}
          {' '}Media ({postForm.mediaType === 'video' ? 'Video' : 'Photo'})
        </label>
        <div className="relative group">
          <div className="w-full h-32 bg-gray-950 border-2 border-dashed border-gray-700 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-pink-500 transition-all">
            {postForm.mediaPreviewUrl ? (
              postForm.mediaType === 'video' ? (
                <video src={postForm.mediaPreviewUrl} className="h-full w-full object-cover rounded-lg" controls />
              ) : (
                <img src={postForm.mediaPreviewUrl} alt="Preview" className="h-full w-full object-cover rounded-lg" />
              )
            ) : (
              <>
                <UploadCloud className="w-8 h-8 text-gray-500 mb-1" />
                <span className="text-xs text-gray-500 font-medium">Click to upload photo or video</span>
              </>
            )}
            <input
              type="file"
              accept="image/*,video/*"
              onChange={handleMediaFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>
        </div>
        {postForm.mediaPreviewUrl && (
          <button
            onClick={handleRemoveMedia}
            className="mt-2 text-xs px-3 py-1.5 rounded-lg bg-red-600/10 text-red-400 hover:bg-red-600/20 border border-red-500/20 font-medium flex items-center gap-1.5 transition-all"
          >
            <Trash2 size={12} /> Remove Media
          </button>
        )}
      </div>
      <button
        onClick={handlePost}
        disabled={loading || !postForm.account || !postForm.mediaFile}
        className={`w-full font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg ${(loading || !postForm.account || !postForm.mediaFile)
            ? 'bg-gray-800 text-gray-500 cursor-not-allowed opacity-50'
            : 'bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white shadow-pink-900/20'
          }`}
      >
        {loading ? 'Posting...' : <><Instagram size={18} /> Post to Instagram</>}
      </button>
    </div>
  );

  const renderLegacyAutomationSection = () => {
    if (platform === 'facebook') {
      return (
        <div className="text-center py-16 text-gray-500 space-y-4">
          <Facebook className="w-14 h-14 mx-auto opacity-30" />
          <div className="max-w-md mx-auto">
            <p className="text-sm font-bold text-gray-400 mb-2">Facebook password login is not supported</p>
            <p className="text-xs text-gray-500 leading-relaxed">
              Meta does not support password automation for Facebook. Please use the <span className="font-bold text-blue-400">Official Meta API</span> tab to post to Facebook Pages.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          {renderLoginSection()}
          <div className="border-t border-gray-800 pt-6">
            {renderAccountsSection()}
          </div>
        </div>
        <div className="space-y-6">
          {accounts.length > 0 ? (
            <>
              {renderPostForm()}
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3"
                >
                  {result.results ? (
                    result.results.map((res, i) => (
                      <div key={i} className="bg-green-900/10 border border-green-500/30 rounded-xl p-4 space-y-2">
                        <p className="text-sm font-bold text-green-400 flex items-center gap-2">
                          <CheckCircle size={16} /> @{res.username}: Posted!
                        </p>
                        {res.data && res.data.url && (
                          <a href={res.data.url} target="_blank" rel="noreferrer"
                            className="text-xs text-blue-400 underline flex items-center gap-1"
                          >
                            <Globe size={12} /> {res.data.url}
                          </a>
                        )}
                        {res.data && res.data.media_id && (
                          <p className="text-xs text-gray-400">Media ID: {res.data.media_id}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="bg-green-900/10 border border-green-500/30 rounded-xl p-4 space-y-2">
                      <p className="text-sm font-bold text-green-400 flex items-center gap-2">
                        <CheckCircle size={16} /> Posted!
                      </p>
                      {result.url && (
                        <a href={result.url} target="_blank" rel="noreferrer"
                          className="text-xs text-blue-400 underline flex items-center gap-1"
                        >
                          <Globe size={12} /> {result.url}
                        </a>
                      )}
                      <p className="text-xs text-gray-400">Media ID: {result.media_id}</p>
                    </div>
                  )}
                  {result.errors && result.errors.length > 0 && (
                    result.errors.map((err, i) => (
                      <div key={i} className="bg-red-900/10 border border-red-500/30 rounded-xl p-4 space-y-2">
                        <p className="text-sm font-bold text-red-400 flex items-center gap-2">
                          <AlertCircle size={16} /> @{err.username}: Failed
                        </p>
                        <p className="text-xs text-red-300">{err.error}</p>
                      </div>
                    ))
                  )}
                </motion.div>
              )}
            </>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-600">
              <div className="text-center">
                <Instagram className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm">Login to an Instagram account first</p>
                <p className="text-xs mt-1">to create and publish posts.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`p-3 rounded-lg text-sm font-medium flex items-center gap-2 ${notification.type === 'success' ? 'bg-green-900/20 text-green-400 border border-green-500/30' :
              notification.type === 'info' ? 'bg-blue-900/20 text-blue-400 border border-blue-500/30' :
                'bg-red-900/20 text-red-400 border border-red-500/30'
              }`}
          >
            {notification.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center border-b border-gray-800 pb-5">
        {/* Method selector */}
        <div className="flex bg-gray-950 p-1 rounded-xl border border-gray-800 shadow-inner text-xs font-semibold">
          <button
            onClick={() => { setApiType('official'); setResult(null); }}
            className={`px-4 py-2 rounded-lg transition-all ${apiType === 'official' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'}`}
          >
            Official Meta API (Recommended)
          </button>
          <button
            onClick={() => { setApiType('legacy'); setResult(null); }}
            className={`px-4 py-2 rounded-lg transition-all ${apiType === 'legacy' ? 'bg-pink-600/20 text-pink-400 border border-pink-500/30' : 'text-gray-400 hover:text-gray-200'}`}
          >
            Legacy Browser Automation (Personal)
          </button>
        </div>

        {/* Platform selector */}
        <div className="flex bg-gray-950 p-1 rounded-xl border border-gray-800 shadow-inner text-xs font-semibold">
          <button
            onClick={() => { setPlatform('instagram'); setResult(null); }}
            className={`px-4 py-2 rounded-lg flex items-center gap-1.5 transition-all ${platform === 'instagram'
              ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-md'
              : 'text-gray-400 hover:text-gray-200'
              }`}
          >
            <Instagram size={14} /> Instagram
          </button>
          <button
            onClick={() => { setPlatform('facebook'); setResult(null); }}
            className={`px-4 py-2 rounded-lg flex items-center gap-1.5 transition-all ${platform === 'facebook'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-gray-400 hover:text-gray-200'
              }`}
          >
            <Facebook size={14} /> Facebook Page
          </button>
        </div>
      </div>

      <div className="bg-gray-900/25 border border-gray-850 rounded-2xl p-6 min-h-[400px]">
        {apiType === 'official' ? renderOfficialAPISection() : renderLegacyAutomationSection()}
      </div>
    </div>
  );
};

export default SocialPosting;
